import { isFilled } from '@prismicio/client';

import type { Content } from '@prismicio/client';

/** Who the learner is. The store's `profile.audience`. */
export type Audience = 'child' | 'teen' | 'adult';

/**
 * A learning topic of either type. The two Prismic types are field-identical;
 * `learning_topic_development` exists only so editors can stage changes.
 */
export type TopicDocument = Content.LearningTopicDocument | Content.LearningTopicDevelopmentDocument;

export type TopicProfile = {
  audience: Audience;
  /** Lower-case language code, as stored on the profile. */
  language: string;
};

/**
 * The `theme` Select values a given audience may see. Verbatim from the current
 * app: a teenager sees `Teen` and `Teen/Adult`, an adult `Adult` and
 * `Teen/Adult`, and a child only `Kids`.
 */
export function themesForAudience(audience: Audience): Array<string> {
  switch (audience) {
    case 'adult':
      return ['Adult', 'Teen/Adult'];
    case 'teen':
      return ['Teen/Adult', 'Teen'];
    case 'child':
      return ['Kids'];
  }
}

/**
 * The topic's language code. `language_ref` is read first; a topic that has not
 * been re-linked yet falls back to the legacy `language` Select, lower-cased
 * (`'EN'` → `'en'`).
 */
export function topicLanguage(doc: TopicDocument): string | null {
  if (isFilled.contentRelationship(doc.data.language_ref)) {
    return doc.data.language_ref.uid ?? null;
  }

  return doc.data.language?.toLowerCase() ?? null;
}

/**
 * The key for a module's progress and analytics, shared by its language variants.
 * Uses `module_key`, then theme and order, then the UID. UIDs don't match across
 * languages, but every translation shares its theme and order.
 */
export function topicModuleKey(doc: TopicDocument): string {
  const key = doc.data.module_key?.trim();

  if (key && key !== '') {
    return key;
  }

  const { theme, order } = doc.data;

  if (theme && typeof order === 'number') {
    return `${theme.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${order}`;
  }

  return doc.uid;
}

/** Topics this profile may see, ordered by the `order` field. Unordered topics sort last. */
export function validTopicsFor<T extends TopicDocument>(profile: TopicProfile, topics: Array<T>): Array<T> {
  const themes = themesForAudience(profile.audience);
  const language = profile.language.toLowerCase();

  return topics
    .filter((topic) => themes.includes(topic.data.theme ?? '') && topicLanguage(topic) === language)
    .sort((a, b) => (a.data.order ?? Infinity) - (b.data.order ?? Infinity));
}

/**
 * The UID of one module's topic in each language it has been authored in,
 * keyed by language code — what the header's language switch needs to keep a
 * learner inside their module instead of returning them to the journey.
 *
 * Matching is on `module_key`, so a topic without one (where `topicModuleKey`
 * falls back to the UID) yields only itself and the switch falls back to the
 * journey. `theme` has to agree too: the kids and the adult cut of a subject
 * are different modules, however they are keyed.
 */
export function moduleTopicUids(topics: Array<TopicDocument>, doc: TopicDocument): Record<string, string> {
  const key = topicModuleKey(doc);
  const uids: Record<string, string> = {};

  for (const candidate of topics) {
    const language = topicLanguage(candidate);

    if (language !== null && topicModuleKey(candidate) === key && candidate.data.theme === doc.data.theme) {
      uids[language] = candidate.uid;
    }
  }

  return uids;
}
